import React, { useEffect, useRef, useState } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import 'moment/locale/de';
import 'moment/locale/es';
import { ADMIN_LANGUAGE_STORAGE_KEY, type AdminLanguage } from '../../i18n';

const LANGUAGES: { code: AdminLanguage; label: string; short: string }[] = [
  { code: 'de', label: 'Deutsch', short: 'DE' },
  { code: 'es', label: 'Español', short: 'ES' },
];

/**
 * Sprach-Umschalter für den Admin-Header (DE/ES). Persistiert die Wahl unter
 * `adminLang` und hält die moment-Locale (Kalender) synchron.
 */
const AdminLanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current: AdminLanguage = i18n.language?.slice(0, 2).toLowerCase() === 'es' ? 'es' : 'de';
  const currentMeta = LANGUAGES.find((l) => l.code === current) ?? LANGUAGES[0];

  // moment-Locale an die aktuelle Sprache koppeln (Kalender-Monats-/Tagesnamen)
  useEffect(() => {
    moment.locale(current);
  }, [current]);

  // Dropdown bei Klick außerhalb schließen
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const changeLanguage = (code: AdminLanguage) => {
    i18n.changeLanguage(code);
    try {
      localStorage.setItem(ADMIN_LANGUAGE_STORAGE_KEY, code);
    } catch (error) {
      console.warn('Error saving admin language preference:', error);
    }
    moment.locale(code);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Sprache · ${currentMeta.label}`}
        title={currentMeta.label}
        className="inline-flex items-center gap-1.5 h-9 px-2.5 rounded-md text-brand-text-muted hover:text-brand-text hover:bg-brand-surface-2 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40"
      >
        <Globe className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden />
        <span className="text-[12px] font-semibold tracking-wide">{currentMeta.short}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
          strokeWidth={2}
          aria-hidden
        />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-2 w-40 bg-brand-surface rounded-lg shadow-lg border border-brand-border overflow-hidden py-1 z-50"
        >
          {LANGUAGES.map((lang) => {
            const isActive = lang.code === current;
            return (
              <li key={lang.code} role="option" aria-selected={isActive}>
                <button
                  type="button"
                  onClick={() => changeLanguage(lang.code)}
                  className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-[13px] transition-colors cursor-pointer ${
                    isActive
                      ? 'bg-brand-accent/15 text-brand-primary font-semibold'
                      : 'text-brand-text hover:bg-brand-surface-2'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className="text-[10px] font-bold tracking-wide text-brand-text-muted w-5">{lang.short}</span>
                    {lang.label}
                  </span>
                  {isActive && <Check className="h-4 w-4 text-brand-primary" strokeWidth={2.25} aria-hidden />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default AdminLanguageSwitcher;
